import { useSyncExternalStore } from 'react'

/**
 * Dev-only: hold every page in its loading state so the skeletons can be
 * looked at next to the loaded page. Toggled with Ctrl+Shift+K (see App) and
 * remembered in localStorage for the tab's lifetime. `import.meta.env.DEV`
 * is a literal `false` in a production build, so all of this folds away.
 */

const KEY = 'keystone:force-skeletons'
const listeners = new Set<() => void>()

function read(): boolean {
  if (!import.meta.env.DEV) return false
  try {
    return localStorage.getItem(KEY) === '1'
  } catch {
    return false
  }
}

export function toggleForcedSkeletons(): boolean {
  const next = !read()
  try {
    localStorage.setItem(KEY, next ? '1' : '0')
  } catch {
    // Nothing to remember it in; the toggle still applies to this render.
  }
  listeners.forEach(l => l())
  return next
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function useForcedSkeletonsDev(): boolean {
  return useSyncExternalStore(subscribe, read, () => false)
}

/**
 * True while skeletons are forced. Chosen once at module load: a production
 * build gets the constant and never subscribes to anything.
 */
export const useForcedSkeletons: () => boolean = import.meta.env.DEV
  ? useForcedSkeletonsDev
  : () => false
